'use client';

import { useEffect, useState } from 'react';
import {
  STAGGER,
  WORD_DURATION,
  normalizePhrases,
  TitleAnimationServer,
  type Phase,
} from './server';

const HOLD_DURATION = 2500;

type Props = {
  className?: string;
  phrases: string[];
};

function useTitleAnimation(phrases: string[]) {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('holding');

  const normalizedPhrases = normalizePhrases(phrases);
  const n = normalizedPhrases[index]?.split(' ').length ?? 0;

  useEffect(() => {
    if (normalizedPhrases.length === 0) {
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    if (phase === 'entering') {
      timer = setTimeout(() => setPhase('holding'), WORD_DURATION + (n - 1) * STAGGER);
    } else if (phase === 'holding') {
      timer = setTimeout(() => setPhase('exiting'), HOLD_DURATION);
    } else {
      timer = setTimeout(
        () => {
          setIndex((i) => (i + 1) % normalizedPhrases.length);
          setPhase('entering');
        },
        WORD_DURATION + (n - 1) * STAGGER
      );
    }

    return () => clearTimeout(timer);
  }, [phase, n, normalizedPhrases.length]);

  return { index, phase };
}

export function TitleAnimationClient({ className, phrases }: Props) {
  const { index, phase } = useTitleAnimation(phrases);

  return (
    <TitleAnimationServer className={className} phrases={phrases} index={index} phase={phase} />
  );
}

export default TitleAnimationClient;
